import React, { useState, useMemo } from "react";

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from "recharts";

import {
  useReactionGraph,
  useWorkingMemoryGraph,
  useShortMemoryGraph,
  useSearchSpanGraph,
  useAnticipationGraph,
  useContrastGraph,
  useVisualAcuityGraph
} from "./GraphHelpers";

import "./PerformanceGraphs.css";

/* =========================
   STAT CALCULATOR
   current / best / average / total tests
   / trend / % change vs last 30 days
========================= */

const computeStats = (data) => {

  if (!data || !data.length) {
    return {
      current: 0,
      best: 0,
      avg: 0,
      total: 0,
      trend: "Stable",
      changePct: 0
    };
  }

  const scores = data.map(d => d.score);

  const current = data[data.length - 1].score;
  const best = Math.max(...scores);
  const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
  const total = data.length;

  const lastDate = new Date(data[data.length - 1].fullDate);

  const cutoff30 = new Date(lastDate);
  cutoff30.setDate(cutoff30.getDate() - 30);

  const cutoff60 = new Date(lastDate);
  cutoff60.setDate(cutoff60.getDate() - 60);

  const recent = data.filter(
    d => new Date(d.fullDate) >= cutoff30
  );

  const prior = data.filter(
    d =>
      new Date(d.fullDate) < cutoff30 &&
      new Date(d.fullDate) >= cutoff60
  );

  const recentAvg =
    recent.length
      ? recent.reduce((a, b) => a + b.score, 0) / recent.length
      : avg;

  const priorAvg =
    prior.length
      ? prior.reduce((a, b) => a + b.score, 0) / prior.length
      : recentAvg;

  let changePct = 0;

  if (priorAvg > 0) {
    changePct = Math.round(
      ((recentAvg - priorAvg) / priorAvg) * 100
    );
  }

  let trend = "Stable";

  if (changePct > 3) {
    trend = "Improving";
  } else if (changePct < -3) {
    trend = "Declining";
  }

  return {
    current,
    best,
    avg: Math.round(avg * 10) / 10,
    total,
    trend,
    changePct
  };

};

/* =========================
   REUSABLE STAT CARD
========================= */

const StatGraphCard = ({
  icon,
  title,
  subtitle,
  data,
  gradientId,
  formatValue,
  yAxisProps
}) => {

  const stats = useMemo(
    () => computeStats(data),
    [data]
  );

  const display = (val) =>
    formatValue ? formatValue(val) : val;

  return (

    <div className="graph-card stat-graph-card">

      <div className="stat-card-top">

        <div className="stat-card-title">

          <h2>
            {icon} {title}
          </h2>

          {subtitle && (
            <p className="stat-subtitle">
              {subtitle}
            </p>
          )}

        </div>

        <div className="stat-badges">

          <div className="stat-badge stat-badge-current">
            <span className="stat-badge-label">Current</span>
            <span className="stat-badge-value">
              {display(stats.current)}
            </span>
          </div>

          <div className="stat-badge stat-badge-best">
            <span className="stat-badge-label">Best</span>
            <span className="stat-badge-value">
              {display(stats.best)}
            </span>
          </div>

          <div
            className={
              "stat-badge stat-badge-change " +
              (stats.changePct >= 0
                ? "positive"
                : "negative")
            }
          >
            {stats.changePct >= 0 ? "↑" : "↓"}{" "}
            {Math.abs(stats.changePct)}%
            <span className="stat-badge-sub">
              vs last 30 days
            </span>
          </div>

        </div>

      </div>

      <ResponsiveContainer width="100%" height={280}>

        <AreaChart data={data}>

          <defs>
            <linearGradient
              id={gradientId}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="5%"
                stopColor="#2563eb"
                stopOpacity={0.35}
              />
              <stop
                offset="95%"
                stopColor="#2563eb"
                stopOpacity={0}
              />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" />

          <XAxis dataKey="date" tick={{ fontSize: 12 }} />

          <YAxis {...(yAxisProps || {})} />

          <Tooltip />

          <Area
            type="monotone"
            dataKey="score"
            stroke="#2563eb"
            strokeWidth={3}
            fill={`url(#${gradientId})`}
            dot={{ r: 4, fill: "#2563eb", strokeWidth: 0 }}
            activeDot={{ r: 6 }}
          />

        </AreaChart>

      </ResponsiveContainer>

      <div className="stat-footer">

        <div className="stat-footer-item">
          <span className="stat-footer-label">
            Average Score
          </span>
          <span className="stat-footer-value">
            {display(stats.avg)}
          </span>
        </div>

        <div className="stat-footer-item">
          <span className="stat-footer-label">
            Total Tests
          </span>
          <span className="stat-footer-value">
            {stats.total}
          </span>
        </div>

        <div className="stat-footer-item">
          <span className="stat-footer-label">
            Trend
          </span>
          <span
            className={
              "stat-footer-trend " +
              stats.trend.toLowerCase()
            }
          >
            {stats.trend}{" "}
            {stats.trend === "Improving" && "↗"}
            {stats.trend === "Declining" && "↘"}
            {stats.trend === "Stable" && "→"}
          </span>
        </div>

      </div>

    </div>

  );

};

const computeReactionStats = (data, key) => {

  const points = data
    .filter(d => d[key] !== null && d[key] !== undefined)
    .map(d => ({ value: d[key], fullDate: d.fullDate }));

  if (!points.length) {
    return { current: 0, best: 0, changePct: 0 };
  }

  const current = points[points.length - 1].value;
  const best = Math.min(...points.map(p => p.value));

  const lastDate = new Date(points[points.length - 1].fullDate);

  const cutoff30 = new Date(lastDate);
  cutoff30.setDate(cutoff30.getDate() - 30);

  const cutoff60 = new Date(lastDate);
  cutoff60.setDate(cutoff60.getDate() - 60);

  const recent = points.filter(
    p => new Date(p.fullDate) >= cutoff30
  );

  const prior = points.filter(
    p =>
      new Date(p.fullDate) < cutoff30 &&
      new Date(p.fullDate) >= cutoff60
  );

  const recentAvg =
    recent.length
      ? recent.reduce((a, b) => a + b.value, 0) / recent.length
      : current;

  const priorAvg =
    prior.length
      ? prior.reduce((a, b) => a + b.value, 0) / prior.length
      : recentAvg;

  let changePct = 0;

  if (priorAvg > 0) {
    // lower ms = better, so a drop in time is a positive (improving) change
    changePct = Math.round(
      ((priorAvg - recentAvg) / priorAvg) * 100
    );
  }

  return { current, best, changePct };

};

const PerformanceGraphs = ({ history }) => {

  const [reactionView, setReactionView] = useState("best");

  const reactionSystemData = useReactionGraph(
    history,
    reactionView
  );

  const reactionTitle = `Reaction System (${
    reactionView === "best" ? "Best" : "Average"
  })`;

  const reactionStats = useMemo(
    () => computeReactionStats(reactionSystemData, "reaction"),
    [reactionSystemData]
  );

  const complexStats = useMemo(
    () => computeReactionStats(reactionSystemData, "complex"),
    [reactionSystemData]
  );

  const workingMemoryGraph = useWorkingMemoryGraph(history);
  const shortMemoryGraph = useShortMemoryGraph(history);
  const searchSpanGraph = useSearchSpanGraph(history);
  const anticipationGraph = useAnticipationGraph(history);
  const contrastGraph = useContrastGraph(history);
  const visualAcuityGraph = useVisualAcuityGraph(history);

  const acuityLabels = {
    1: "6/60", 2: "6/36", 3: "6/24", 4: "6/18",
    5: "6/12", 6: "6/9", 7: "6/6", 8: "6/5",
    9: "6/4.5", 10: "6/4", 11: "6/3.5", 12: "6/3", 13: "6/2.5"
  };

  return (

    <section className="progress-graphs">

      <div className="section-header">
        <h2>Performance Journey</h2>
        <p>Track your vision and cognitive improvements.</p>
      </div>

      {/* =========================
          REACTION SYSTEM
      ========================= */}

      <div className="graph-card">

        <div className="reaction-header">

          <h2>{reactionTitle}</h2>

          <select
            className="reaction-select"
            value={reactionView}
            onChange={(e) => setReactionView(e.target.value)}
          >
            <option value="best">Best</option>
            <option value="average">Average</option>
          </select>

        </div>

        <div className="stat-badges reaction-stat-badges">

          <div className="stat-badge stat-badge-current reaction-badge">
            <span className="stat-badge-label">Reaction · Current</span>
            <span className="stat-badge-value">
              {reactionStats.current} ms
            </span>
          </div>

          <div className="stat-badge stat-badge-best reaction-badge">
            <span className="stat-badge-label">Reaction · Best</span>
            <span className="stat-badge-value">
              {reactionStats.best} ms
            </span>
          </div>

          <div
            className={
              "stat-badge stat-badge-change reaction-badge " +
              (reactionStats.changePct >= 0 ? "positive" : "negative")
            }
          >
            {reactionStats.changePct >= 0 ? "↑" : "↓"}{" "}
            {Math.abs(reactionStats.changePct)}%
            <span className="stat-badge-sub">vs last 30 days</span>
          </div>

          <div className="stat-badge stat-badge-current complex-badge">
            <span className="stat-badge-label">Complex · Current</span>
            <span className="stat-badge-value">
              {complexStats.current} ms
            </span>
          </div>

          <div className="stat-badge stat-badge-best complex-badge">
            <span className="stat-badge-label">Complex · Best</span>
            <span className="stat-badge-value">
              {complexStats.best} ms
            </span>
          </div>

          <div
            className={
              "stat-badge stat-badge-change complex-badge " +
              (complexStats.changePct >= 0 ? "positive" : "negative")
            }
          >
            {complexStats.changePct >= 0 ? "↑" : "↓"}{" "}
            {Math.abs(complexStats.changePct)}%
            <span className="stat-badge-sub">vs last 30 days</span>
          </div>

        </div>

        <ResponsiveContainer width="100%" height={400}>

          <LineChart data={reactionSystemData}>

            <CartesianGrid strokeDasharray="3 3" />

            <XAxis
              dataKey="date"
              interval={Math.ceil(reactionSystemData.length / 8)}
              tick={{ fontSize: 12 }}
              minTickGap={30}
            />

            <YAxis />
            <Tooltip />
            <Legend />

            <Line
              type="monotone"
              dataKey="reaction"
              name="Reaction"
              stroke="#2563eb"
              strokeWidth={3}
              connectNulls
              dot={{ r: 4 }}
            />

            <Line
              type="monotone"
              dataKey="complex"
              name="Complex"
              stroke="#ef4444"
              strokeWidth={3}
              connectNulls
              dot={{ r: 4 }}
            />

          </LineChart>

        </ResponsiveContainer>

      </div>

      <div className="graph-grid">

        <StatGraphCard
          icon="👁"
          title="Visual Acuity"
          subtitle="Track your visual acuity performance over time."
          data={visualAcuityGraph}
          gradientId="gradAcuity"
          formatValue={(v) => acuityLabels[v] || v}
          yAxisProps={{
            ticks: [1,2,3,4,5,6,7,8,9,10,11,12,13],
            tickFormatter: (v) => acuityLabels[v]
          }}
        />

        <StatGraphCard
          icon="⚫"
          title="Contrast Sensitivity"
          subtitle="Track your contrast sensitivity over time."
          data={contrastGraph}
          gradientId="gradContrast"
        />

        <StatGraphCard
          icon="🧠"
          title="Working Memory"
          subtitle="Track your working memory performance over time."
          data={workingMemoryGraph}
          gradientId="gradWorking"
          yAxisProps={{
            allowDecimals: false,
            domain: [
              dataMin => Math.max(1, dataMin - 1),
              dataMax => dataMax + 1
            ]
          }}
        />

        <StatGraphCard
          icon="🧠"
          title="Short-Term Visual Memory"
          subtitle="Track your short-term visual memory."
          data={shortMemoryGraph}
          gradientId="gradShort"
          yAxisProps={{
            allowDecimals: false,
            domain: [
              dataMin => Math.max(0, dataMin - 1),
              dataMax => dataMax + 1
            ]
          }}
        />

        <StatGraphCard
          icon="🔍"
          title="Search Span"
          subtitle="Track your visual search span performance."
          data={searchSpanGraph}
          gradientId="gradSearch"
          yAxisProps={{
            allowDecimals: false,
            domain: [
              dataMin => Math.max(0, dataMin - 1),
              dataMax => dataMax + 1
            ]
          }}
        />

        <StatGraphCard
          icon="🎯"
          title="Anticipation"
          subtitle="Track your anticipation and prediction accuracy."
          data={anticipationGraph}
          gradientId="gradAnticipation"
          formatValue={(v) => `${v}%`}
        />

      </div>

    </section>

  );

};

export default PerformanceGraphs;